import { motion } from 'framer-motion';
import { Users, Music, Package, Sparkles, Lightbulb, Volume2, Rocket } from 'lucide-react';

// M³ Framework Cards
const m3Framework = [
  {
    icon: Users,
    title: 'Mascot',
    description: 'We create memorable character IP designed to connect emotionally with audiences and form the foundation for scalable entertainment brands.',
  },
  {
    icon: Music,
    title: 'Music',
    description: 'We build original music identities, signature sounds, and audio worlds that strengthen recognition, deepen connection, and create lasting recall.',
  },
  {
    icon: Package,
    title: 'Merchandise',
    description: 'We extend strong character concepts into collectibles, toys, and consumer products that turn creative worlds into commercial ecosystems.',
  },
];

// How We Work Steps
const howWeWork = [
  {
    icon: Lightbulb,
    step: '01',
    title: 'Create the Character',
    description: 'We design distinctive mascot-led IP with clear visual identity.',
  },
  {
    icon: Volume2,
    step: '02',
    title: 'Build the Sound',
    description: 'We create original music and sonic branding that gives each world emotional impact and recognition.',
  },
  {
    icon: Rocket,
    step: '03',
    title: 'Expand the World',
    description: 'We shape the concept into merchandise, collectibles, partnerships, and licensing opportunities.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut',
    },
  },
};

const GLOW_COLORS = ['glow-purple', 'glow-blue', 'glow-pink'];

export const WhySoundmoo = () => {
  return (
    <section
      id="why-soundmoo"
      data-testid="why-soundmoo-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="soundmoo-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-sm font-medium tracking-widest uppercase text-white/60 mb-4 block"
          >
            Why Soundmoo
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] mb-6">
            Powering Characters, Music & Merchandise
          </h2>
          <p className="text-lg text-white/80 max-w-3xl mx-auto leading-relaxed">
            Soundmoo is a character IP studio building the next generation of entertainment-driven products. 
            Through our M³ Framework — Mascot, Music, Merchandise — we transform ideas into visually distinctive 
            characters, sonic identities, and collectible products designed for global licensing.
          </p>
        </motion.div>

        {/* M³ Framework Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="flex justify-center mb-10"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#FBBF24]/20 to-[#F59E0B]/20 border border-[#FBBF24]/40">
            <Sparkles className="w-4 h-4 text-[#FBBF24]" />
            <span className="text-sm font-semibold text-[#FBBF24] tracking-wide">THE M³ FRAMEWORK</span>
            <Sparkles className="w-4 h-4 text-[#FBBF24]" />
          </div>
        </motion.div>

        {/* M³ Framework Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          {m3Framework.map((item, index) => (
            <motion.div
              key={item.title}
              data-testid={`m3-card-${index}`}
              variants={cardVariants}
              className={`glow-card ${GLOW_COLORS[index % GLOW_COLORS.length]} p-6 cursor-pointer`}
            >
              {/* Icon Container */}
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-5 transition-transform duration-300 relative z-10">
                <item.icon className="w-7 h-7 text-white" />
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-white font-['Outfit'] mb-3 relative z-10">
                {item.title}
              </h3>
              <p className="text-white/70 text-sm leading-relaxed relative z-10">
                {item.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Supporting Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xl md:text-2xl font-semibold text-white/90 font-['Outfit'] italic">
            "Turning characters into cultural products."
          </p>
        </motion.div>

        {/* Closing Paragraph */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <p className="text-base text-white/70 max-w-3xl mx-auto leading-relaxed">
            By combining character design with original music, Soundmoo creates intellectual property that can expand 
            across toys, digital media, partnerships, licensing, and brand collaborations. We sit at the intersection 
            of character design, music identity, and collectible culture.
          </p>
        </motion.div>

        {/* How We Work Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl md:text-4xl font-bold text-white font-['Outfit'] mb-4">
            How We Work
          </h3>
          <p className="text-base text-white/80 max-w-2xl mx-auto leading-relaxed">
            We develop original character IP, collaborate with partner brands, and build music-led identities 
            that are designed for licensing and product-led growth. Every Soundmoo concept is developed with 
            world-building, audience connection, and long-term commercial potential in mind.
          </p>
        </motion.div>

        {/* How We Work Steps */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {howWeWork.map((step, index) => (
            <motion.div
              key={step.title}
              data-testid={`how-we-work-${index}`}
              variants={cardVariants}
              className="glass-card-dark p-6 text-center"
            >
              {/* Step Number */}
              <div className="text-4xl font-bold text-white/20 font-['Outfit'] mb-3">
                {step.step}
              </div>
              
              {/* Icon */}
              <div className="w-12 h-12 rounded-full bg-[#19A4FB]/20 flex items-center justify-center mx-auto mb-4">
                <step.icon className="w-6 h-6 text-[#19A4FB]" />
              </div>

              {/* Content */}
              <h4 className="text-lg font-semibold text-white font-['Outfit'] mb-2">
                {step.title}
              </h4>
              <p className="text-white/70 text-sm leading-relaxed">
                {step.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
